import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import HistorySection from 'Components/HistorySection';
import HistoryRowListing from 'Components/HistoryRowListing';
import { Container, Item } from 'Components/Grid';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import HistoryRowControls from 'Components/HistoryRowControls';
import { maxNpiScores, npiFormFields, npiHistoryFields } from '../Form/NPI/definitions';
import Unit from 'Components/Unit';
import { exists } from 'neuro-utils';
import { omit } from 'Utility/ramdaReplacement';
import { npi } from 'neuro-schemas';

const { calculateNPIFrequencyAndSeverityScore, calculateNPIStressScore } = npi.utils;

const NPIHistory = ({ documents, startEdit, view }: IOwnProps): React.JSX.Element => {
  return (
    <HistorySection
      headerText={<FormattedMessage id={'diagnosticSurvey.npi.title'} />}
      newButton={
        <DocumentCreationButton
          name="npi"
          text={'diagnosticSurvey.npi.newNPI'}
          onClick={startEdit({}, 'npi')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
    >
      <HistoryRowListing
        documents={documents}
        headers={
          <Item xs={true}>
            <Container>
              {npiHistoryFields.map(f => (
                <Item
                  key={f}
                  xs={2}
                  style={{
                    paddingRight: '0.5rem' /** Bigger padding or smaller screen might need manual word breaking */,
                  }}
                >
                  <FormattedMessage id={`diagnosticSurvey.npi.${f}`} />
                </Item>
              ))}
            </Container>
          </Item>
        }
        contentFormat={d => (
          <Item xs={true}>
            <Container>
              {npiFormFields['summary']?.map((f: string) => {
                const score = {
                  frequencyAndSeverityTotalScoreFromDelusionsToAbnormalMotoricBehavior:
                    calculateNPIFrequencyAndSeverityScore(omit(['sleepDisorders', 'appetiteAndEatingDisorders'], d)),
                  stressTotalScoreFromDelusionsToAbnormalMotoricBehavior: calculateNPIStressScore(
                    omit(['sleepDisorders', 'appetiteAndEatingDisorders'], d),
                  ),
                  frequencyAndSeverityTotalScoreFromSleepDisorders: calculateNPIFrequencyAndSeverityScore({
                    sleepDisorders: d.sleepDisorders,
                  }),
                  stressTotalScoreFromSleepDisorders: d.sleepDisorders?.stress,
                  frequencyAndSeverityTotalScoreFromEatingAndAppetiteDisorders: calculateNPIFrequencyAndSeverityScore({
                    appetiteAndEatingDisorders: d.appetiteAndEatingDisorders,
                  }),
                  stressTotalScoreFromEatingAndAppetiteDisorders: d.appetiteAndEatingDisorders?.stress,
                }[f];
                return (
                  <Item key={f} xs={2}>
                    <Unit unit={`/ ${maxNpiScores[f]}`} fontWeight={'bold'}>
                      <React.Fragment>{exists(score) ? score : '-'}</React.Fragment>
                    </Unit>
                  </Item>
                );
              })}
            </Container>
          </Item>
        )}
        makeDate
        makeControls
        historyRowControls={d => <HistoryRowControls document={d} startEdit={startEdit} view={view} />}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'npi'>[];
  startEdit: StartEdit<IDocumentWithTypeV2<'npi'>>;
  view?: IView;
}

export default NPIHistory;
